(ns cms.editor
  (:require [reagent.core :as r]
            [cemerick.url :as url]
            [clojure.string :as str]
            
            [cms.fetch :refer [fetch]]
            [cms.data :as data :refer [nest unnest shallow-diff]]
            [cms.editor.core :refer [resolve-schema
                                     schema-editor
                                     search&]]
            [cms.editor.array]
            [cms.editor.ordered-elements]
            [cms.editor.primitives]
            [cms.editor.storage]
            
            [common.util :refer [new-uuid]]
            
            [firebase.functions :as fns 
             :refer-macros [defcallable]]
            [promises.core :refer-macros [await->]]))

(defcallable cms_submit)

(defonce worktree& 
  (r/atom {:url nil
           :data nil}))

(defonce schema& 
  (r/atom {}))

(await-> 
  (fetch 
    (fns/get-https-function-url "cms_schema"))
  :body
  (then [schema] (reset! schema& schema))
  (catch [err]
    (js/console.error 
      err (pr-str (ex-data err)))))

(defn search-field [search&]
  [:div {:style {:margin "1em"}}
   [:input {:type :text 
            :name :search
            :value @search&
            :placeholder "🔎  Type to filter..."
            :style {:padding "0.5em"
                    :width :auto}
            :on-key-down
            (fn [e] (when (= (.. e -key) "Escape")
                      (reset! search& nil)))
            :on-change
            (fn [e] (reset! search& (.. e -target -value)))}]])

(defn editor [{:keys [url] :as props} node&]
  (let [defs @schema&
        
        s (reduce 
            (fn [schema url-segment]
              (let [{:strs [properties 
                            additionalProperties
                            items]} schema]
                (resolve-schema 
                  defs
                  (or 
                    (get properties url-segment)
                    additionalProperties
                    items))))
            (resolve-schema defs defs) 
            (remove str/blank? (str/split url #"/")))]
    (reset! search& nil)
    (if (= :pending @node&)
      [:p "..."]
      [:<> 
       [search-field search&]
       [schema-editor url defs s node&]])))

(defn save-button [clean-url payload 
                   & {:keys [on-success on-error disabled text]}]
  [:button.vspace 
   {:disabled disabled
    :on-click 
    (fn [evt]
      (await-> 
        (cms_submit 
          {:channel "public"
           :path clean-url
           :content (or payload {})})
        (then [{:strs [error message code]}] 
              (cond 
                error (on-error code message)
                :else (on-success)))))}
   (or text "Submit")])

(defn update-editor-state! [clean-url worktree&]
  (await-> 
    (fetch 
      (-> (fns/get-https-function-url "cms_content")
          (url/url)
          (update :query 
                  assoc 
                  "nocache" (new-uuid)
                  "p" clean-url)))
    :body 
    (then [fetched-data]                     
          (swap! 
            worktree&
            (fn [{:keys [data url stored]}]
              (let [fetched-unnest
                    (into {} (unnest clean-url fetched-data))
                    
                    stored-unnest 
                    (into {} (unnest stored))
                    
                    worktree-unnest 
                    (into {} (unnest data))
                    
                    merged-stored 
                    (merge stored-unnest fetched-unnest)
                    
                    merged-unnest
                    (merge merged-stored worktree-unnest)]
                
                {:url          clean-url
                 :data         (nest merged-unnest)
                 :stored       (nest merged-stored)}))))))

(defn view [props url worktree&]
  (let [segments (remove str/blank? (str/split url #"/"))
        clean-url (str/join "/" segments)]
    
    (update-editor-state! clean-url worktree&)
    
    [:div.editor.column 
     (into [:ul.breadcrumbs
            [:li
             [:a {:href "#/edit/"} "⛁"]]] 
           (for [i (range (count segments))]
             [:li 
              [:a {:href 
                   (str "#/edit/" 
                        (str/join "/" (take (inc i) segments)))}
               (nth segments i)]]))
     [(fn []
        (let [data-path (into [:data] segments)
              {:keys [data stored]} @worktree&
              changes? (not= data stored)
              payload (get-in data segments)
              
              unnested-changes
              (when changes? 
                (shallow-diff 
                  (unnest data)
                  (unnest stored)))
              
              nested-changes
              (nest unnested-changes)
              
              change-words
              (str (count unnested-changes) " change"
                   (when (seq (rest unnested-changes)) "s"))
              
              submit-button
              [save-button clean-url payload
               :on-success 
               (fn [_] 
                 (swap! 
                   worktree& 
                   (fn [wt]                       
                     (assoc-in 
                       wt                                       
                       (into [:stored] segments)
                       (get-in wt data-path)))))
               :on-error (fn [code message] (js/alert (str code ":" message)))
               :disabled (not changes?)
               :text (if changes? (str "Submit (" change-words ")") "No changes")]]
          [:<>
           
           submit-button
           
           (when changes? 
             [:div 
              {:style {:align-items :center
                       :width "100%"}}
              [:a 
               {:on-click 
                #(let [changes (.getElementById js/document "review-changes")]
                   (set! (.-open changes) true)
                   (.scrollIntoView changes))
                } "Review " change-words]])
           
           [editor (assoc props :url url)
            (r/cursor worktree& data-path)]
          	
           (when changes? 
             [:<> 
              submit-button
              
              (when changes? 
                [:details#review-changes 
                 {:style {:display :block}}
                 [:summary 
                  [:h3 (count unnested-changes) " Changes"]]
                 
                 [:p.deemph "Click on a key to revert all changes underneath"]
                 
                 [(fn walk [changes path]
                    (let [revert! 
                          (fn [k]
                            (swap! 
                              worktree& 
                              (fn [wt]
                                (assoc-in 
                                  wt (into [:data] (conj path k))
                                  (get-in wt (into [:stored] (conj path k)))))))]
                      [:table 
                       [:tbody 
                        (for [[k c] changes]
                          (into [:tr {:key k}] 
                                (cond 
                                  (map? (or c {}))
                                  [[:th 
                                    [:a 
                                     {:on-click #(revert! k)}
                                     (str k)]]
                                   [:td (walk c (conj path k))]]
                                  
                                  :else 
                                  (let [[b a] c]
                                    [[:th 
                                      [:a
                                       {:on-click #(revert! k)} 
                                       (str k)]] 
                                     [:td [:span
                                           {:style {:gap "0.5ch"}} 
                                           [:span 
                                            {:style 
                                             {:text-decoration :line-through
                                              :color "#800"}} 
                                            (pr-str a)]
                                           "➡️"
                                           [:span 
                                            {:style {:color "#080"}}
                                            (pr-str b)]]]]))))]])) 
                  nested-changes []]])])]))]]))